/**
 * Question API Service
 * Handles API calls for question-related operations
 */
import apiService from './apiService';
import questionFormatService from './questionFormatService';

class QuestionService {
  basePath = '/api/question';

  /**
   * Get the count of available questions based on filter criteria
   * @param {Object} filterData - Question filter parameters matching QuestionFilterRequestModel
   * @param {boolean} [filterData.hasImage] - Optional filter for questions with images
   * @param {boolean} [filterData.aiGenerated] - Optional filter for AI generated questions
   * @param {number[]} [filterData.difficulty] - Optional difficulty levels to filter by
   * @param {number[]} [filterData.categoryIds] - Optional list of category IDs to filter by
   * @param {number[]} [filterData.folderIds] - Optional list of folder IDs to filter by
   * @param {boolean} [filterData.neverSeen] - Optional filter for questions the user has never seen
   * @param {boolean} [filterData.answeredIncorrectly] - Optional filter for questions the user has answered incorrectly
   * @param {boolean} [filterData.answeredCorrectly] - Optional filter for questions the user has answered correctly
   * @param {boolean} [filterData.seenBefore] - Optional filter for questions the user has seen before
   * @param {number} [filterData.examTypeId] - The exam type ID to filter by
   * @returns {Promise<number>} - Count of matching questions
   */
  async getQuestionsCount(filterData = {}) {
    return apiService.post(`${this.basePath}/count`, filterData);
  }
  
  /**
   * Fetch detailed question information including answers and explanations
   * @param {number} questionId - The ID of the question to fetch details for
   * @returns {Promise<Object>} - Detailed question object with answers and explanations
   */
  async getQuestionDetails(questionId) {
    try {
      if (!questionId) {
        console.error('Cannot fetch question details: questionId is undefined');
        return null;
      }
      
      // console.log(`Fetching details for question ID: ${questionId}`);
      const questionDetails = await apiService.get(`${this.basePath}/${questionId}`);
      // console.log('Question details fetched:', questionDetails);
      
      return this.formatQuestion(questionDetails);
    } catch (error) {
      console.error(`Error fetching question details for ID ${questionId}:`, error);
      return null;
    }
  }
  
  /**
   * Format a question from API response to consistent structure
   * @param {Object} questionData - Raw question data from API
   * @returns {Object} - Formatted question object
   */
  formatQuestion(questionData) {
    return questionFormatService.formatQuestionFromResponse(questionData);
  }
  /**
   * Get current question with proper formatting from questions array
   * @param {Array} questions - Array of questions
   * @param {number} currentIndex - Current question index
   * @param {Array} mockQuestions - Fallback mock questions if API data is not available
   * @returns {Object} - Formatted current question
   */
  getCurrentQuestion(questions, currentIndex, mockQuestions = []) {
    // console.log('getCurrentQuestion called with:', {
    //   questionsLength: questions ? questions.length : 'questions is null/undefined',
    //   currentIndex,
    //   mockQuestionsLength: mockQuestions ? mockQuestions.length : 'mockQuestions is null/undefined'
    // });
    
    // Enhanced check to handle various edge cases
    if (questions && Array.isArray(questions) && questions.length > 0 && 
        currentIndex >= 0 && currentIndex < questions.length) {
      const question = questions[currentIndex];
      // console.log('Using real API data for question:', question.id || question.questionId);
      return this.formatQuestion(question);
    }
    
    // If we reach here, log that we're falling back to mock data and why
    console.warn('Falling back to mock data because:', {
      reason: !questions ? 'questions is null/undefined' : 
              !Array.isArray(questions) ? 'questions is not an array' :
              questions.length === 0 ? 'questions array is empty' :
              currentIndex < 0 ? 'currentIndex is negative' :
              currentIndex >= questions.length ? `currentIndex (${currentIndex}) >= questions.length (${questions.length})` :
              'unknown reason'
    });
    
    // Fall back to mock data if API data is not available
    return mockQuestions[currentIndex % mockQuestions.length];
  }

  /**
   * Get formatted options for a question
   * @param {Object} question - Question object
   * @returns {Array} - Formatted options
   */
  getFormattedOptions(question) {
    return questionFormatService.getFormattedOptions(question);
  }

  /**
   * Extract clues from formatted question text
   * @param {string} formattedText - Formatted question text
   * @returns {Array} - Extracted clues
   */
  extractClues(formattedText) {
    return questionFormatService.extractClues(formattedText);
  }

  /**
   * Get question text with highlighted clues
   * @param {Object} question - Question object
   * @param {boolean} showClues - Whether to show clues
   * @returns {string} - Formatted question text
   */
  getQuestionWithClues(question, showClues) {
    return questionFormatService.getQuestionWithClues(question, showClues);
  }

  /**
   * Get the ID of the correct answer
   * @param {Object} question - Question object
   * @returns {number|null} - ID of correct answer or null
   */
  getCorrectAnswerId(question) {
    return questionFormatService.getCorrectAnswerId(question);
  }

  /**
   * Get explanation text for a question
   * @param {Object} question - Question object
   * @returns {string} - Explanation text
   */
  getExplanationText(question) {
    return questionFormatService.getExplanationText(question);
  }

  /**
   * Check if the selected answer is correct
   * @param {Object} question - Question object
   * @param {string} selectedAnswer - Selected answer (e.g., "A", "B", "C")
   * @returns {boolean} - Whether answer is correct
   */
  isSelectedAnswerCorrect(question, selectedAnswer) {
    // For API data questions
    if (question && question.answers && Array.isArray(question.answers)) {
      const selectedAnswerIndex = selectedAnswer ? selectedAnswer.charCodeAt(0) - 65 : -1;
      return selectedAnswerIndex >= 0 && 
             selectedAnswerIndex < question.answers.length && 
             (question.answers[selectedAnswerIndex].isCorrect || false);
    }
    
    // For mock data questions
    return selectedAnswer === question.correctAnswer;
  }

  /**
   * Get the current user's rating for a specific question
   * @param {number} questionId - The ID of the question
   * @returns {Promise<number>} - Rating value (1-5) or throws error if no rating exists
   */
  async getQuestionRating(questionId) {
    try {
      const rating = await apiService.get(`${this.basePath}/${questionId}/rate`);
      return rating;
    } catch (error) {
      if (error.status === 404) {
        // No rating exists for this question
        return null;
      }
      throw error;
    }
  }

  /**
   * Submit or update a rating for a specific question
   * @param {number} questionId - The ID of the question
   * @param {number} rating - Rating value (1-5)
   * @returns {Promise<void>} - Success response
   */
  async submitQuestionRating(questionId, rating) {
    if (rating < 1 || rating > 5) {
      throw new Error('Rating must be between 1 and 5');
    }

    const requestData = { rate: rating };
    return apiService.post(`${this.basePath}/${questionId}/rate`, requestData);
  }
}

// Export singleton instance
export default new QuestionService();
